import { DecimalPipe } from "@angular/common";
import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { AuthService } from "app/auth/auth.service";
import {
  AlertController,
  ModalController,
  Platform,
  PopoverController,
} from "ionic-angular";
import moment from "moment-timezone";
import { UtilityService } from "providers/utility-service/utility-service";
import { Observable } from "rxjs/Observable";
import { ISubscription } from "rxjs/Subscription";
import { NavigationService } from "../../../../navigation/navigation.service";
import { AssessmentDataService } from "../../../../providers/asmt-data-service/asmt-data-service";
import { StateManagementService } from "../../../../providers/state-management/state-management.service";
import { convertToVoiceTextarea } from "../../../../utils/convert-to-voice-textarea";
import { IntercardDataService } from "../intercard-data/intercard-data.service";

const dateTimeFormat = "MM/DD/YYYY HH:mm";

/**
 * Generated class for the ListCardComponent component.
 *
 * See https://angular.io/api/core/Component for more info on Angular
 * Components.
 */
@Component({
  selector: "list-card",
  templateUrl: "list-card.html",
})
export class ListCardComponent implements OnInit, OnDestroy {
  @Input() assessmentId: string;
  @Input() sectionId: string;
  @Input() config;
  @Input() disabled: boolean = false;
  @Input() visitType: string = "";
  @Input() visitCompleted: boolean = false;
  @Input() visitInProgress: boolean = false;

  id: string;
  name: string;
  componentId: string;
  label: string;
  access: string;
  tooltip: object;
  colHeaders: string[];
  conditionalFormatting: object;
  deltaFields: string[];
  rawData: any[] = [];
  dataRows: any[] = [];
  dummyRow: object;
  prevRow: {};
  declinable: boolean;
  editable: boolean = false;
  manualAdd: boolean = true;
  seeMore: boolean = true;
  seeMoreText: boolean = true;
  seeMoreLength: number = 0;
  moreActions: boolean = false;

  allowExternalData: boolean = false;
  delta: {};

  dataPages: Array<object[]> = [];
  pageSize: number = 5;
  currentPage: number = 0;

  cardDepObs: Observable<any> = null;
  cardDepObsSubscription: ISubscription;

  // SUP-19954: retain the last raw payload so rows can be re-derived when the
  // tenant timezone arrives. The WHOLE payload is kept, not just rawData —
  // incomingData.previous is only stored parsed (prevRow), so rebuilding from
  // rawData alone would drop the previous-reading column.
  lastIncomingData: any = null;
  renderedTz: string = null;
  userInfoSub: ISubscription;

  bmiHeight;
  heightTimeout;
  private endDate: string;

  constructor(
    public modalCtrl: ModalController,
    public dataService: AssessmentDataService,
    public decimalPipe: DecimalPipe,
    public plt: Platform,
    private alertCtrl: AlertController,
    public translatePipe: TranslateService,
    public intercard: IntercardDataService,
    public popover: PopoverController,
    public utilityService: UtilityService,
    public stateData: StateManagementService,
    public navSvc: NavigationService,
    public authSvc: AuthService
  ) {}

  ngOnInit(): void {
    this.id = this.config.id;
    this.name = this.config.cardName;
    this.componentId = this.config.componentId;
    this.label =
      this.config.label !== undefined
        ? this.config.label
        : this.config.cardName;
    this.access = this.config.access;
    this.tooltip = this.config.tooltip;
    this.colHeaders = this.config.data.listHeaders.filter(this.filterHeaders);
    if (this.config.hospiceAideToggle) {
      this.colHeaders = this.config.data.listHeadersToggle;
      this.addHAToggleToInputs();
    }
    this.conditionalFormatting = this.config.data.conditionalFormatting;
    this.deltaFields = this.config.data.deltaFields || [];
    this.declinable = this.config.declinable;
    this.moreActions = this.config.moreActions;
    if (this.config.seeMore) {
      this.seeMore = !this.config.seeMore;
      this.seeMoreLength = this.config.seeMoreLength;
    }

    if (this.config.pageSize) {
      this.pageSize = this.config.pageSize;
    }

    if (this.config.addNewBtn === true) {
      this.config.onAddNew = this.onAddNew.bind(this);
    }

    if (this.config.editable) {
      this.editable = this.config.editable;
    }
    if (this.config.manualAdd !== undefined && this.config.manualAdd !== null) {
      this.manualAdd = this.config.manualAdd;
    }
    if (this.config.allowExternalData) {
      this.allowExternalData = this.config.allowExternalData;
    }

    this.dummyRow = { id: "" };
    let dummyData = {};
    for (let header of this.colHeaders) {
      dummyData[header] = "-";
    }
    this.dummyRow["values"] = dummyData;

    // SUP-19954 — the tenant timezone loads asynchronously after first render
    // (navigation-header.getLocationTimeZone). Dates here are formatted into
    // strings at @Input-set time, so without this they stay frozen on whatever
    // was resolved during the startup race. Re-derive the rows once the tz
    // lands. Subscribed after colHeaders is populated because UserInfoChange is
    // a BehaviorSubject and replays synchronously on subscribe, and
    // rebuildRows -> parseDataRow iterates colHeaders. Guarded on a CHANGED tz
    // so the replay is not a redundant rebuild.
    this.userInfoSub = this.authSvc.UserInfoChange.subscribe((user) => {
      const tz = user && user.timeZoneOrg;
      if (tz && tz !== this.renderedTz && this.lastIncomingData) {
        this.rebuildRows(this.lastIncomingData);
      }
    });

    if (this.allowExternalData) {
      this.cardDepObs = this.intercard.register(this.name);
      this.cardDepObsSubscription = this.cardDepObs.subscribe((data) => {
        // this.cardDepObs.subscribe(data => {
        // console.log( "Received emitted data from other card!", data );
        if (data === undefined) {
          return;
        } else if (data["declined"] === true || data["noChanges"] === true) {
          // console.log(data);

          let accept = () => {
            // console.log("accepting");
            let obj = {};
            if (data["declined"]) {
              obj["acceptedDecline"] = true;
            }
            if (data["noChanges"]) {
              obj["acceptedNoChanges"] = true;
            }
            this.intercard.emitData("declines", obj);
          };

          let reject = () => {
            // console.log( "List modal REJECTED!" );

            let obj = {};
            if (data["declined"]) {
              obj["rejectedDeclined"] = true;
            }
            if (data["noChanges"]) {
              obj["rejectedNoChanges"] = true;
            }
            this.intercard.emitData("declines", obj);
          };

          if (this.id === "CL010.172") {
            this.presentListForm(
              { category: "patientDeclinedHospiceAideServices" },
              accept,
              reject
            );
          } else {
            this.presentListForm(
              { category: "patientDeclinedAssessment" },
              accept,
              reject
            );
          }
          //this.presentListForm({category: 'patientDeclinedAssessment'}, accept, reject);
          return;
        } else if (data["adls"] === true) {
          this.presentListForm({ category: "adlNeeds" });
          return;
        } else if (data["calculateBmi"] === true) {
          // console.log( "We need to update for BMI!", data['height'] );

          let height = data["height"]["inches"];
          // console.log("this.assessmentid before bmi", this.assessmentId);
          this.updateBmiValues(height);

          return;
        }

        let newData = {};
        for (let header of this.colHeaders) {
          if (data[header]) {
            newData[header] = data[header];
          }
        }

        this.addData(newData);
      });
    }
  }

  @Input()
  set data(incomingData: any) {
    this.lastIncomingData = incomingData;
    this.rebuildRows(incomingData);
  }

  private rebuildRows(incomingData: any) {
    this.renderedTz = this.currentOrgTz();
    if (incomingData) {
      // Call some service to load rows and prevRow
      this.dataRows = [];
      if (incomingData.data) {
        this.rawData = incomingData.data;
        for (let data of incomingData.data) {
          let info = {
            id: data.id,
            seemore: this.seeMore,
            seemoretext: this.seeMoreText,
          };
          let values = this.parseDataRow(data);
          info["values"] = values;
          this.dataRows.push(info);
        }
      }

      if (
        this.config.id === "CL010.262" &&
        this.config.hospiceAideToggle &&
        this.dataRows.length > 1
      ) {
        this.sortHATaskData();
      }

      if (this.componentId === "symptomImpactSummary") {
        this.loadSymptomImpactSummaryData();
      }

      this.updateDataPages();

      if (
        incomingData.previous &&
        this.componentId !== "symptomImpactSummary"
      ) {
        let prevData = incomingData.previous;
        if (prevData.hasOwnProperty("weight")) {
          this.updateBmi(prevData);
        }
        let prevInfo = this.parseDataRow(prevData);
        this.prevRow = prevInfo;
        this.prevRow["id"] = prevData.id;
      }
      this.updateDelta();
    }
    if (incomingData === null && this.componentId === "symptomImpactSummary") {
      this.loadSymptomImpactSummaryData();
    }
  }

  loadSymptomImpactSummaryData() {
    const sectionLinkMap = {
      Pain: "pain",
      "Shortness of Breath": "respiratory",
      Anxiety: "neurological",
      Agitation: "neurological",
      Nausea: "gastrointestinal",
      Vomiting: "gastrointestinal",
      Diarrhea: "gastrointestinal",
      Constipation: "gastrointestinal",
    };
    const symptomOrder = Object.keys(sectionLinkMap);

    if (this.visitInProgress) {
      this.dataService
        .getSymptomImpactSummary(this.assessmentId)
        .subscribe((data) => {
          this.dataRows = data.map((item) => ({
            values: {
              symptom: item.symptom,
              thisAssessment: item.thisAssessmentValue || "Not Assessed",
              mostRecentAssessmentValue:
                item.mostRecentAssessmentValue || "Not Assessed",
              mostRecentAssessmentDate: item.mostRecentAssessmentDate || "-",
            },
            section: sectionLinkMap[item.symptom],
          }));
          this.dataPages = [this.dataRows];
        });
    } else {
      this.dataRows = this.dataRows.map((item) => ({
        values: {
          symptom: item.values.symptom,
          thisAssessment: ["-", null, undefined].includes(
            item.values.thisAssessment
          )
            ? "Not Assessed"
            : item.values.thisAssessment,
        },
        section: sectionLinkMap[item.values.symptom],
      }));
    }

    this.dataRows.sort((a, b) => {
      const aSymptom = a.values?.symptom;
      const bSymptom = b.values?.symptom;
      return symptomOrder.indexOf(aSymptom) - symptomOrder.indexOf(bSymptom);
    });
  }

  sortHATaskData() {
    let nullEndDateTaskList = this.dataRows.filter(
      (row) => row.values.endDate === "-"
    );
    let taskList = this.dataRows.filter((row) => row.values.endDate !== "-");
    this.dataRows = [];

    if (nullEndDateTaskList.length > 0) {
      nullEndDateTaskList = nullEndDateTaskList.sort(
        (a, b) =>
          new Date(b.values.startDate).getTime() -
          new Date(a.values.startDate).getTime()
      );
      this.dataRows = JSON.parse(JSON.stringify(nullEndDateTaskList));
    }

    if (taskList.length > 0) {
      taskList = taskList.sort((a, b) => {
        if (
          new Date(b.values.endDate).getTime() ===
          new Date(a.values.endDate).getTime()
        ) {
          return (
            new Date(b.values.startDate).getTime() -
            new Date(a.values.startDate).getTime()
          );
        } else {
          return (
            new Date(b.values.endDate).getTime() -
            new Date(a.values.endDate).getTime()
          );
        }
      });
      this.dataRows.push(...JSON.parse(JSON.stringify(taskList)));
    }
  }

  addHAToggleToInputs() {
    for (let key in this.config.inputs) {
      this.config.inputs[key].hospiceAideToggle = this.config.hospiceAideToggle;
    }
  }

  rowHighlighted(col, value) {
    if (this.conditionalFormatting) {
      let formatting = this.conditionalFormatting[col];
      if (formatting) {
        if (
          formatting.type == "greaterThan" &&
          formatting.refValue !== undefined &&
          value > formatting.refValue
        ) {
          return true;
        }
      }
    }

    return false;
  }

  getPageNumber() {
    return this.currentPage + 1;
  }

  presentListForm(data?: object, accept?: Function, reject?: Function): void {
    let newConfig;
    try {
      newConfig = JSON.parse(JSON.stringify(this.config));
    } catch (e) {
      newConfig = this.config;
    }

    let config = { config: newConfig };

    if (this.config.addNewBtn === true) {
      config.config["onAddNew"] = this.onAddNew.bind(this);
    }

    if (data !== undefined) {
      config["data"] = data;
      config.config.dataRows = this.dataRows;
    } else {
      if (this.config.hospiceAideToggle) {
        let defaultData: any = {};
        defaultData.frequency = {
          timeUnit: "visit(s)",
          numerator: 1,
          denominator: 1,
        };
        config["data"] = defaultData;
        config.config.dataRows = this.dataRows;
        config.config.editData = false;
      }
    }

    // Convert to voiceTextarea if needed
    config = convertToVoiceTextarea(config);

    let listModal = this.modalCtrl.create("InputModalPage", config, {
      enableBackdropDismiss: false,
    });
    listModal.onDidDismiss((data) => {
      if (data !== undefined && data !== null) {
        if (data.hasOwnProperty("weight")) {
          this.updateBmi(data);
        }

        this.addData(data);
        if (accept) {
          accept();
        }
      } else if (reject) {
        reject();
      }
    });
    listModal.present();
  }

  onAddNew(data: any) {
    this.addData(data);
  }

  updateDelta() {
    if (this.prevRow && this.dataRows[0]) {
      let deltaFields = this.deltaFields;
      let dataRows = this.dataRows;
      let prevRow = this.prevRow;
      this.delta = {};
      for (let header of this.colHeaders) {
        let diff: any = "-";

        if (deltaFields.indexOf(header) == -1) {
          this.delta[header] = diff;
          continue;
        }

        if (header == "date" && moment(dataRows[0].values[header])) {
          let end = moment(dataRows[0].values[header]);
          let start = moment(prevRow[header]);
          let duration = moment.duration(start.diff(end));
          diff = duration.humanize() + " ago";
        } else if (
          !isNaN(dataRows[0].values[header]) &&
          !isNaN(prevRow[header])
        ) {
          diff = dataRows[0].values[header] - prevRow[header];
          diff = parseFloat(this.decimalPipe.transform(diff, "1.0-2"));
          if (diff > 0) {
            diff = "+" + diff;
          }
        }
        this.delta[header] = diff;
      }
    }
  }

  flattenIncomingData(data: object): object {
    let flatData = {};
    for (let key of Object.keys(data)) {
      if (typeof data[key] == "object") {
        let innerObj = data[key];
        if (innerObj == undefined) {
          continue;
        }

        if (
          key !== "reportedOn" &&
          key !== "lastReading" &&
          key !== "frequency" &&
          key !== "bereavementCharacteristics"
        ) {
          for (let innerKey of Object.keys(innerObj)) {
            if (key == "leftArmOther" || key == "rightArmOther") {
              let newKey = key;
              flatData[newKey] = innerObj[innerKey];
            } else {
              let newKey =
                key + innerKey.replace(/\b\w/g, (l) => l.toUpperCase());
              flatData[newKey] = innerObj[innerKey];
            }
          }
        } else if (key == "reportedOn" || key == "lastReading") {
          flatData[key] = innerObj["date"] + "T" + innerObj["time"];
        } else if (key == "frequency") {
          // console.log( "Detected frquency input, parsing to string: ", innerObj );
          let finalValue = "";
          if (innerObj.numerator) {
            finalValue +=
              innerObj.numerator + this.translatePipe.instant(" time(s) per ");
          }

          if (innerObj.denominator) {
            finalValue += innerObj.denominator + " ";
          }

          if (innerObj.timeUnit) {
            finalValue += this.translatePipe.instant(innerObj.timeUnit);
          }

          flatData[key] = finalValue;
        } else if (key === "bereavementCharacteristics") {
          let arr = data[key].map((item) => this.translatePipe.instant(item));
          flatData[key] = arr.join(", ");
        }
      } else {
        flatData[key] = data[key];
      }
    }
    return flatData;
  }

  // getUser() can return null before the session is hydrated, so never
  // dereference it directly (navigation-header and brief-bar both guard it).
  private currentOrgTz(): string {
    const user = this.authSvc.getUser();
    return user && user.timeZoneOrg ? user.timeZoneOrg : null;
  }

  // SUP-19954: render a date in the TENANT timezone. The tenant tz arrives
  // asynchronously after login, so it can be unset on first render. Returning ""
  // rather than falling back to the browser zone keeps a near-midnight reading
  // from showing the wrong calendar day; the UserInfoChange subscription in
  // ngOnInit re-renders the rows once the tz lands.
  private formatInOrgTz(value: any): string {
    const orgTimezone = this.currentOrgTz();
    if (!orgTimezone) {
      return "";
    }
    return moment(value).tz(orgTimezone).format(dateTimeFormat);
  }

  parseDataRow(data: any) {
    let values: any = {};
    let flatData = this.flattenIncomingData(data);
    for (let header of this.colHeaders) {
      let val = "-";
      if (flatData[header] || flatData[header] === 0) {
        if (
          header === "date" ||
          header === "reportedOn" ||
          header === "lastReading"
        ) {
          flatData[header] = this.formatInOrgTz(flatData[header]);
        }

        if (this.config.hospiceAideToggle) {
          if (header === "startDate" && flatData[header] === "-") {
            flatData[header] = this.formatInOrgTz(flatData["date"]);
          }
        }

        val = flatData[header];
      } else {
        if (this.config.hospiceAideToggle) {
          if (header === "startDate") {
            flatData[header] = this.formatInOrgTz(flatData["date"]);
            val = flatData[header];
          }
        }
      }
      values[header] = val;
    }
    if (data.historyId) {
      values["historyId"] = data.historyId;
    }
    return values;
  }

  addData(data: any): void {
    if (data) {
      if (!data.hasOwnProperty("historyId")) {
        data.historyId = this.utilityService.uuid();
      }

      if (this.name === "weight" && this.bmiHeight) {
        let weight = data.weight.lbs;
        if (weight) {
          data.bmi = this.calculateBmi(weight, this.bmiHeight);
        }
      }

      if (this.config.hospiceAideToggle) {
        data.startDate = moment(new Date()).format("MM/DD/YYYY");
      }

      if (this.name === "reportableIssues") {
        //check if duplicate reportableIssues
        for (let key of this.dataRows) {
          if (key.values.issueType === data.issueType) {
            return;
          }
        }
      }
      if (this.assessmentId != null) {
        try {
          this.dataService
            .storeRecord(
              this.assessmentId,
              this.sectionId,
              this.id,
              data,
              this.label
            )
            .subscribe((returnData) => {
              if (!returnData) {
                return;
              }
              let newData = {
                id: returnData.id,
                seemore: this.seeMore,
                seemoretext: this.seeMoreText,
              };
              newData["values"] = this.parseDataRow(returnData);

              this.rawData.unshift(returnData);
              this.dataRows.unshift(newData);
              this.updateDataPages();
              this.updateDelta();
            });
        } catch (error) {
          //console.log( "Error during data submission and building.", error );
        }
      }
    }
  }

  editRow(row: object): void {
    let modalConfig: any = {};
    try {
      modalConfig = JSON.parse(JSON.stringify({ config: this.config }));
    } catch (e) {
      modalConfig = { config: this.config };
    }
    // In case of Edit list item we don't need "Add New" button
    if (modalConfig.config.addNewBtn) {
      delete modalConfig.config.addNewBtn;
    }

    let values = this.rawData.filter((record) => record.id == row["id"])[0];
    if (values !== null || values !== undefined) {
      let inputKeys = Object.keys(this.config.inputs);
      let editData = {};

      for (let key of inputKeys) {
        if (values[key]) {
          if (key === "issueType") {
            values[key] = this.translatePipe.instant(values[key]);
          }
          editData[key] = values[key];
        } else {
          editData[key] = null;
        }
      }
      modalConfig["data"] = editData;
    }

    modalConfig.config.dataRows = this.dataRows;
    if (this.config.hospiceAideToggle) {
      modalConfig.config.editData = true;
      modalConfig.config.currentRow = modalConfig.data;
    }

    // Convert to voiceTextarea if needed
    modalConfig = convertToVoiceTextarea(modalConfig);

    let listModal = this.modalCtrl.create("InputModalPage", modalConfig, {
      enableBackdropDismiss: false,
    });
    listModal.onDidDismiss((data) => {
      if (data !== undefined) {
        if (data.hasOwnProperty("weight")) {
          this.updateBmi(data);
        }

        let oldRow = JSON.parse(JSON.stringify(row));
        if (this.config.hospiceAideToggle) {
          data.startDate = oldRow.values.startDate;
          if (oldRow.values.endDate && oldRow.values.endDate !== "-") {
            data.endDate = oldRow.values.endDate;
          }

          if (
            oldRow.values &&
            oldRow.values.category &&
            oldRow.values.task &&
            oldRow.values.assistance &&
            oldRow.values.frequency
          ) {
            data.editTask = true;
            let frequency = oldRow.values.frequency.split(" ");
            data.oldTaskTitle =
              this.translatePipe.instant(oldRow.values.category) +
              ", " +
              this.translatePipe.instant(oldRow.values.task) +
              ", " +
              this.translatePipe.instant(oldRow.values.assistance) +
              ", " +
              frequency[0] +
              " x " +
              frequency[3] +
              " " +
              frequency[4];
          }
        }
        if (oldRow.values.historyId) {
          data.historyId = oldRow.values.historyId;
        }
        this.editData(row, row["id"], data);

        if (this.config.hospiceAideToggle) {
          data.editTask = false;
          setTimeout(() => {
            this.editDataWithOutUpdatingRow(row, row["id"], data);
          }, 2000);
        }
      }
    });
    listModal.present();
  }

  moreOptions(ev: any, row: any) {
    let hideDeleteOption = false;
    let hospiceAideToggle = false;
    let hideEditOption = false;
    if (this.config.hospiceAideToggle === true) {
      hideDeleteOption = true;
      hospiceAideToggle = true;
      if (row.values && row.values.endDate && row.values.endDate !== "-") {
        hospiceAideToggle = false;
        hideEditOption = true;
      } else if (this.editable == false) {
        hideEditOption = true;
        //   let count = 0;
        //   for(let eachRow of this.dataRows)
        //   {
        //     let dataLength = this.dataRows.length;
        //     if(eachRow.values.endDate && eachRow.values.endDate !== '-')
        //     {
        //       count++;
        //       if(count === dataLength - 1)
        //       {
        //         hospiceAideToggle = false;
        //       }
        //     }
        //   }
      }
    }

    const popover = this.popover.create(
      "CareTeamsOptionsPage",
      { hideDeleteOption, hospiceAideToggle, hideEditOption },
      { cssClass: "care-teams-options-popover" }
    );
    popover.onDidDismiss((data) => {
      if (data) {
        if (data === "delete") {
          this.deleteRow(row.id);
        } else if (data === "edit") {
          this.editRow(row);
        } else if (data === "cancel") {
          this.endDate = "-";
          let minDateValue;
          if (row.values && row.values.startDate) {
            minDateValue = row.values.startDate;
          }
          let endDatePopup = this.modalCtrl.create("DatetimePopupPage", {
            minDateValue,
          });
          endDatePopup.onDidDismiss((date) => {
            if (date && date !== "Invalid date") {
              this.endDate = date;
              this.cancelRow(row);
            }
          });
          endDatePopup.present();
        }
      }
    });
    popover.present({ ev });
  }

  updateBmi(data) {
    let height = sessionStorage.getItem("heightForBmi");
    if (height) {
      height = JSON.parse(height);
      height = height["inches"];
      let weight = data.weight.lbs;
      if (height && weight) {
        let bmi = this.calculateBmi(weight, height);
        data.bmi = bmi;
      }
    }
  }

  editData(
    oldRow: object,
    rowId: string,
    data: any,
    caller = "don't care"
  ): void {
    if (data) {
      try {
        this.dataService
          .editRecord(this.assessmentId, this.sectionId, this.id, rowId, data)
          .subscribe((returnData) => {
            let newData = {
              id: returnData.id,
              seemore: this.seeMore,
              seemoretext: this.seeMoreText,
            };
            newData["values"] = this.parseDataRow(returnData);

            let rawData = this.rawData;
            for (let index in rawData) {
              if (rawData[index]["id"] === returnData.id) {
                rawData[index] = returnData;
                break;
              }
            }

            this.dataRows.splice(this.dataRows.indexOf(oldRow), 1, newData);
            if (this.config.hospiceAideToggle) {
              this.sortHATaskData();
            }
            this.updateDataPages();
            this.updateDelta();
          });
      } catch (error) {
        console.log("Error during data submission and building.", error);
      }
    }
  }

  editDataWithOutUpdatingRow(
    oldRow: object,
    rowId: string,
    data: any,
    caller = "don't care"
  ): void {
    if (data) {
      try {
        this.dataService
          .editRecord(this.assessmentId, this.sectionId, this.id, rowId, data)
          .subscribe((returnData) => {});
      } catch (error) {
        console.log("Error during data submission and building.", error);
      }
    }
  }

  deleteRow(id: string) {
    let alert = this.alertCtrl.create({
      title: this.translatePipe.instant("confirmDeleteRecord"),
      buttons: [
        {
          text: "Cancel",
        },
        {
          text: "Proceed",
          handler: () => {
            this.dataService
              .deleteRecord(this.assessmentId, this.sectionId, this.id, id)
              .subscribe(() => {
                this.rawData = this.rawData.filter((row) => row.id !== id);
                this.dataRows = this.dataRows.filter((row) => row.id !== id);
                this.updateDataPages();
              });
          },
        },
      ],
    });
    alert.present();
  }

  cancelRow(row) {
    let cancelData: any = {};
    let frequency = {
      numerator: String,
      denominator: String,
      timeUnit: String,
    };
    for (let header of this.config.data.listHeadersToggle) {
      cancelData[header] = row.values[header];
      if (header === "endDate") {
        cancelData[header] = this.endDate;
      } else if (header === "frequency") {
        let splitValues = row.values[header].split(" ");
        frequency.numerator = splitValues[0];
        frequency.denominator = splitValues[3];
        frequency.timeUnit = splitValues[4];
        cancelData[header] = frequency;
      } else if (header === "haComments" && row.values[header] === "-") {
        cancelData[header] = "";
      }
      cancelData["resolvedCarePlan"] = true;
    }
    if (row.values.historyId) {
      cancelData.historyId = row.values.historyId;
    }
    this.editData(row, row.id, cancelData);

    //calling edit data function again because in the assessment backend code
    //when it triggers the care plan issue it creates duplicate resovled issue in order to stop we need to create resolved careplan issue
    cancelData["resolvedCarePlan"] = false;
    setTimeout(() => {
      this.editDataWithOutUpdatingRow(row, row.id, cancelData);
    }, 2000);
  }

  updateDataPages() {
    if (this.componentId === "symptomImpactSummary") {
      this.dataPages = [this.dataRows]; // no need to paginate for this card
      return;
    }

    this.dataPages = [];

    let dataRows = this.dataRows;
    let pageSize = this.pageSize;
    let dataPages = this.dataPages;
    let pageIndex = -1;
    for (let i = 0; i < dataRows.length; i++) {
      let j = i % pageSize;
      if (j == 0) {
        dataPages.push([]);
        pageIndex++;
      }
      dataPages[pageIndex][j] = dataRows[i];
    }
    if (pageIndex > 0) {
      let endingRow = dataRows.length % pageSize;
      if (endingRow > 0) {
        for (let i = endingRow; i < pageSize; i++) {
          dataPages[pageIndex][i] = this.dummyRow;
        }
      }
    }

    if (this.currentPage > dataRows.length % pageSize) {
      this.prevPage();
    }
  }

  firstPage() {
    if (this.currentPage !== 0) {
      this.currentPage = 0;
    }
  }

  prevPage() {
    if (this.currentPage > 0) {
      this.currentPage--;
    }
  }

  setPage(page) {
    if (
      page >= 0 &&
      page <= this.dataPages.length &&
      page !== this.currentPage
    ) {
      this.currentPage = page;
    }
  }

  nextPage() {
    if (this.currentPage < this.dataPages.length - 1) {
      this.currentPage++;
    }
  }

  lastPage() {
    if (this.currentPage !== this.dataPages.length - 1) {
      this.currentPage = this.dataPages.length - 1;
    }
  }

  updateBmiValues(incomingHeight) {
    let calculate = (height) => {
      let index = 0;
      for (let oldRow of this.dataRows) {
        let weight = oldRow.values.weightLbs;
        let oldBmi = oldRow.values.bmi;

        let bmi = this.calculateBmi(weight, height);

        if (oldBmi === bmi) {
          continue;
        }

        let data = JSON.parse(JSON.stringify(oldRow.values));

        data.bmi = bmi;
        data = {
          ...data,
          weight: { lbs: oldRow.values.weightLbs, kg: oldRow.values.weightKg },
        };
        delete (data as any).weightKg;
        delete (data as any).weightLbs;
        index++;
        console.log(index);
        setTimeout(() => {
          this.editData(oldRow, oldRow["id"], data, "BMI Values");
        }, index * 150);
      }

      if (this.prevRow) {
        let weight = this.prevRow["weightLbs"];
        let oldBmi = this.prevRow["bmi"];

        let bmi = this.calculateBmi(weight, height);

        if (oldBmi !== bmi) {
          let data = JSON.parse(JSON.stringify(this.prevRow));
          data.bmi = bmi;
          this.prevRow = data;
        }
      }
    };

    if (this.heightTimeout) {
      clearTimeout(this.heightTimeout);
    }
    this.bmiHeight = incomingHeight;
    this.heightTimeout = setTimeout(() => {
      calculate(this.bmiHeight);
      this.heightTimeout = null;
    }, 1500);
  }

  calculateBmi(weight, height) {
    let bmi = 703 * (weight / (height * height));
    bmi = Math.round(bmi * 100) / 100;
    return bmi;
  }

  ngOnDestroy() {
    if (this.userInfoSub) {
      this.userInfoSub.unsubscribe();
      this.userInfoSub = undefined;
    }
    if (this.cardDepObsSubscription) {
      this.cardDepObsSubscription.unsubscribe();
      this.cardDepObs = null;
    }
    if (this.componentId === "symptomImpactSummary" && !this.visitCompleted) {
      this.dataService.syncSymptomImpactSummary(this.assessmentId);
    }
  }

  validateVisitType() {
    var result = false;
    if (this.visitType == "Continuous Care Visits") result = true;
    if (this.visitType == "EMC") result = true;
    if (this.visitType == "In-Patient") result = true;
    return result;
  }

  onSectionClick(sectionId: string) {
    this.navSvc.goToInternalPage("SectionPage", {
      patChartId: this.stateData.getChartId(),
      assessmentId: this.stateData.getAssessmentId(),
      sectionId: sectionId,
    });
  }

  filterHeaders = (header) => {
    if (
      !this.visitInProgress &&
      ["mostRecentAssessmentDate", "mostRecentAssessmentValue"].includes(
        header.toString()
      )
    ) {
      return false;
    }
    return true;
  };
}



// WEBPACK FOOTER //
// ./src/components/configurable/cards/list-card/list-card.ts